import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchWorkspaceModules, updateWorkspaceModule } from '@/api/workspaces.api';

/**
 * useWorkspaceModules
 * 
 * Hook for managing workspace modules with React Query.
 * Provides fetching, toggling, and optimistic updates.
 * 
 * @param workspaceId - The workspace ID (optional)
 * @returns Object with modules array, isLoading, and toggleModule function
 */
export function useWorkspaceModules(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ['workspace-modules', workspaceId];

  // Fetch modules
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<string[]> => {
      if (!workspaceId) {
        return [];
      }
      return await fetchWorkspaceModules(workspaceId);
    },
    enabled: !!workspaceId,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });

  // Update module mutation with optimistic updates
  const mutation = useMutation({
    mutationFn: async ({ moduleKey, enabled }: { moduleKey: string; enabled: boolean }) => {
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }
      return await updateWorkspaceModule(workspaceId, moduleKey, enabled);
    },
    onMutate: async ({ moduleKey, enabled }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousModules = queryClient.getQueryData<string[]>(queryKey) || [];

      // Optimistically update the cache
      const updatedModules = enabled
        ? [...previousModules.filter((m) => m !== moduleKey), moduleKey] // Add module if not present
        : previousModules.filter((m) => m !== moduleKey); // Remove module

      queryClient.setQueryData<string[]>(queryKey, updatedModules);

      // Return context with previous value for rollback
      return { previousModules };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousModules) {
        queryClient.setQueryData<string[]>(queryKey, context.previousModules);
      }
    },
    onSuccess: () => {
      // Invalidate cache to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Toggle module function
  const toggleModule = (moduleKey: string, enabled: boolean) => {
    mutation.mutate({ moduleKey, enabled });
  };

  return {
    modules: query.data || [], // Never undefined, always array
    isLoading: query.isLoading,
    toggleModule,
    isToggling: mutation.isPending,
    error: query.error || mutation.error,
  };
}
